import { create } from "zustand";
import { dealsApi } from "@/api/deals-api";
import { transformDeals } from "@/services/deals-service";
import { Deal, DealTransformed } from "@/lib/schema/deal-schema";

export interface DealsBrowse {
  page?: number;
  limit?: number;
  search?: string;
  sort?: string;
  dir?: "asc" | "desc";
  /** Saved view to scope the list to — a curated list's membership. */
  view?: string;
  /** Row data only — see `getSimpleDealList`. */
  lean?: boolean;
  /** Serialised FilterNode[] — see components/deals/filters.ts */
  filters?: string;
}

/**
 * Whether a browse asked for a subset of the workspace rather than all of it.
 *
 * `filters` arrives already serialised (`serializeFilters`), and an empty set
 * serialises to `"[]"` rather than being omitted — so an empty array is not the
 * same as "no filters were sent" and both have to read as unnarrowed here.
 */
export const isNarrowedBrowse = (browse: DealsBrowse | null): boolean => {
  if (!browse) return false;
  if (browse.search || browse.view) return true;
  return !!browse.filters && browse.filters !== "[]";
};

export interface FetchOptions {
  /**
   * Don't advertise this fetch as loading.
   *
   * For polls that run on a timer: they would otherwise put the table into its
   * loading state every few seconds, so a run in progress would make the page
   * flicker rather than sit still while the answers fill in.
   */
  silent?: boolean;
}

export interface DealsPagination {
  page: number;
  limit: number;
  total: number;
  total_pages: number;
}

interface DealsListStore {
  // State
  deals: Record<string, DealTransformed>;
  isLoading: boolean;
  /**
   * Whether a fetch has ever succeeded for the current workspace. Separates the
   * first load, which has nothing to show and needs a skeleton, from a refetch,
   * which should keep the table on screen.
   */
  hasLoaded: boolean;
  /** Which workspace the retained `deals` belong to. */
  loadedWorkspaceId: string | null;
  /** What the last fetch asked for, so `refreshDeals` can ask for it again. */
  lastBrowse: DealsBrowse | null;
  /**
   * What the browse that produced the retained `deals` asked for.
   *
   * Distinct from `lastBrowse`, which is set when a request *starts*. The rows
   * on screen belong to the previous request until the new one lands, so
   * anything reasoning about what is currently displayed — above all the
   * choice between "no deals in this workspace" and "nothing matched" — has to
   * ask this rather than live UI state.
   */
  loadedBrowse: DealsBrowse | null;
  error: string | null;
  lastFetchTime: number | null;
  /**
   * Present only when the last fetch asked to page. The kanban board and the
   * portfolio view deliberately load everything, and get undefined here.
   */
  pagination: DealsPagination | null;

  // Actions
  fetchDeals: (
    workspaceId: string,
    browse?: DealsBrowse,
    options?: FetchOptions
  ) => Promise<void>;
  /**
   * Re-run the last browse. Anything that just wants the table brought up to
   * date after an action must use this rather than `fetchDeals(workspaceId)`,
   * which asks for the whole workspace and so replaces a filtered or curated
   * table with every deal in it.
   */
  refreshDeals: (workspaceId: string, options?: FetchOptions) => Promise<void>;
  updateDeal: (dealId: string, updates: Partial<DealTransformed>) => void;
  clearDeals: () => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
}

const initialState = {
  deals: {},
  isLoading: false,
  hasLoaded: false,
  loadedWorkspaceId: null,
  lastBrowse: null,
  loadedBrowse: null,
  error: null,
  lastFetchTime: null,
  pagination: null,
};

/**
 * Every browse change fires a request, and they can land out of order — a slow
 * unfiltered load resolving after a fast filtered one would leave the table
 * showing results nobody asked for. Only the newest request may write.
 */
let latestRequestId = 0;

export const useDealsListStore = create<DealsListStore>()((set, get) => ({
  ...initialState,

  fetchDeals: async (
    workspaceId: string,
    browse?: DealsBrowse,
    options?: FetchOptions
  ) => {
    const requestId = ++latestRequestId;

    // Within a workspace, `deals` is deliberately left in place. Clearing it on
    // every fetch is what made each filter, sort or page change blank the whole
    // table and re-mount it; the previous rows stay up until the new ones are
    // ready. Across workspaces they must go — they belong to the old pipeline.
    const isNewWorkspace = get().loadedWorkspaceId !== workspaceId;

    set({
      ...(options?.silent ? {} : { isLoading: true }),
      error: null,
      lastBrowse: browse ?? null,
      ...(isNewWorkspace
        ? { deals: {}, hasLoaded: false, pagination: null, loadedBrowse: null }
        : {}),
    });

    try {
      // Fetch simple deal list from API
      const { deals: rawDeals, pagination } = await dealsApi.getSimpleDealList(
        workspaceId,
        browse
      );

      if (requestId !== latestRequestId) return;

      // Transform the deals using the existing service
      const transformedDeals = transformDeals(rawDeals as Deal[]);

      set({
        deals: transformedDeals,
        isLoading: false,
        hasLoaded: true,
        loadedWorkspaceId: workspaceId,
        loadedBrowse: browse ?? null,
        lastFetchTime: Date.now(),
        pagination: pagination ?? null,
      });
    } catch (error) {
      if (requestId !== latestRequestId) return;

      console.error("Error fetching deals:", error);
      set({
        error: error instanceof Error ? error.message : "Failed to fetch deals",
        isLoading: false,
      });
    }
  },

  refreshDeals: async (workspaceId: string, options?: FetchOptions) => {
    await get().fetchDeals(workspaceId, get().lastBrowse ?? undefined, options);
  },

  updateDeal: (dealId: string, updates: Partial<DealTransformed>) => {
    set((state) => ({
      deals: {
        ...state.deals,
        [dealId]: {
          ...state.deals[dealId],
          ...updates,
        },
      },
    }));
  },

  clearDeals: () => {
    set(initialState);
  },

  setLoading: (loading: boolean) => {
    set({ isLoading: loading });
  },

  setError: (error: string | null) => {
    set({ error });
  },
}));

// Selectors
export const selectDeals = (state: DealsListStore) => state.deals;
export const selectIsLoading = (state: DealsListStore) => state.isLoading;
export const selectError = (state: DealsListStore) => state.error;
export const selectDealsList = (state: DealsListStore) =>
  Object.values(state.deals);
export const selectDealById = (dealId: string) => (state: DealsListStore) =>
  state.deals[dealId];
